"use client";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  UserPlus, ArrowRight, Search, Upload,
  Download, FileText, FileSpreadsheet, Eye,
  ArrowUpDown, ChevronUp, ChevronDown, Loader2,
} from "lucide-react";
import CreateTeacherModal from "./CreateTeacherModal";
import EditProfileModal from "./EditProfileModal";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import { TeacherPermissionModal } from "./TeacherPermissionModal";
import ImportSuccessDialog from "./ImportSuccessDialog";
import ImportProgressDialog from "./ImportProgressDialog";
import TeacherImportManualDialog from "./TeacherImportManualDialog";
import TeacherProfile from "./TeacherProfile";
import { useTeachers, getTeacherInitial } from "./hooks/useTeachers";
import { useTeacherExport } from "./hooks/useTeacherExport";
import { getFullName } from "../../../utils/urlUtils";
import Pagination from "../../Common/Pagination";

function TeachersContent() {
  const navigate = useNavigate();
  const {
    teachers, leaves, error, setError, successMessage, setSuccessMessage,
    isEditModalOpen, setIsEditModalOpen,
    isModalOpen, setIsModalOpen,
    isPermissionModalOpen, setIsPermissionModalOpen,
    selectedTeacher, setSelectedTeacher,
    selectedTeacherProfile, setSelectedTeacherProfile,
    searchQuery, setSearchQuery,
    currentAcademicYear, academicYears,
    academicYearFilter, setAcademicYearFilter,
    classFilter, setClassFilter,
    isImporting, importSuccess, setImportSuccess,
    showProgressDialog, importProgress,
    sortColumn, sortDirection,
    activeTab, setActiveTab,
    isDeleteDialogOpen, setIsDeleteDialogOpen,
    subjectsList, classesData, sectionData,
    subjectFilter, setSubjectFilter,
    statusFilter, setStatusFilter,
    designationFilter, setDesignationFilter,
    departmentFilter, setDepartmentFilter,
    wingFilter, setWingFilter,
    totalTeachers, designations, departments, wings,
    currentPage, setCurrentPage,
    itemsPerPage, setItemsPerPage,
    isLoading, isImportManualOpen, setIsImportManualOpen,
    fetchTeachers, fetchAllTeachersForExport,
    handleViewProfile, handleUpdateTeacher, handleDeleteTeacher,
    handleFileUpload, handleSort,
  } = useTeachers();

  const {
    exportColumns, previewData, isPreviewOpen, setIsPreviewOpen,
    handleExportCSV, handleExportExcel, handleExportPDF, handlePreviewExport,
  } = useTeacherExport({ fetchAllTeachersForExport });

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <ArrowUpDown className="h-4 w-4 text-gray-400 shrink-0" />;
    return sortDirection === "asc"
      ? <><ChevronUp className="h-4 w-4 text-[#0263cc] shrink-0" /><span className="text-[10px] font-normal text-gray-500 normal-case">{column === "name" ? "(A→Z)" : "(Low→High)"}</span></>
      : <><ChevronDown className="h-4 w-4 text-[#0263cc] shrink-0" /><span className="text-[10px] font-normal text-gray-500 normal-case">{column === "name" ? "(Z→A)" : "(High→Low)"}</span></>;
  };

  return (
    <div className="space-y-4">
      {selectedTeacherProfile ? (
        <div>
          <Button
            onClick={() => {
              setSelectedTeacherProfile(null);
              navigate("/dashboard/teachers");
            }}
            className="mb-4 bg-gray-500 hover:bg-gray-600 text-white"
          >
            Back to Teachers Management
          </Button>
          <TeacherProfile
            teacher={selectedTeacherProfile}
            leaves={leaves}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            onEdit={() => { setSelectedTeacher(selectedTeacherProfile); setIsEditModalOpen(true); }}
            onDelete={() => setIsDeleteDialogOpen(true)}
            onPermissions={() => setIsPermissionModalOpen(true)}
            fetchTeachers={fetchTeachers}
          />
        </div>
      ) : (
        <>
          <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
            <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10 pb-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-[#0263cc] text-xl font-semibold">Teacher Management</CardTitle>
                  <CardDescription>View and manage teacher information</CardDescription>
                </div>
                <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                  <div className="relative w-full sm:w-auto">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search teachers..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 pr-4 py-2 bg-white border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg w-full sm:w-64"
                    />
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-10 h-10 p-0 rounded-full text-[#0263cc] hover:text-[#0263cc] hover:border-[#0263cc] border-[#0263cc]/20" title="Sort by name">
                        <ArrowUpDown className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[200px]">
                      <DropdownMenuRadioGroup
                        value={sortDirection === "asc" ? "aToZ" : "zToA"}
                        onValueChange={(value) => handleSort(value === "aToZ" ? "name" : "name")}
                      >
                        <DropdownMenuRadioItem value="aToZ">A to Z</DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="zToA">Z to A</DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-10 h-10 p-0 rounded-full text-red-600 hover:text-red-600 hover:border-red-600 border-[#0263cc]/20" title="Export">
                        <Download className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[200px]">
                      <DropdownMenuItem onClick={handleExportCSV}>
                        <FileText className="w-4 h-4 mr-2" /> Export as CSV
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportExcel("xlsx")}>
                        <FileSpreadsheet className="w-4 h-4 mr-2" /> Export as xlsx
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportExcel("xls")}>
                        <FileSpreadsheet className="w-4 h-4 mr-2" /> Export as xls
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handlePreviewExport}>
                        <Eye className="w-4 h-4 mr-2" /> Preview Export
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <div className="relative">
                    <button
                      className={`p-2 rounded-full hover:bg-gray-100 transition-colors duration-200 ${isImporting ? "opacity-50 cursor-not-allowed" : ""}`}
                      disabled={isImporting}
                      onClick={() => setIsImportManualOpen(true)}
                      title={isImporting ? "Importing..." : "Import Teachers"}
                    >
                      <Upload className="w-5 h-5" />
                    </button>
                    <input id="file-upload" type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleFileUpload} disabled={isImporting} />
                    {isImporting && <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-blue-500 animate-pulse"></span>}
                  </div>

                  <Button
                    onClick={() => { setSelectedTeacher(null); setIsModalOpen(true); }}
                    className="w-10 h-10 p-0 rounded-full bg-green-500 hover:bg-green-600 transition-colors duration-200"
                    title="Add New Teacher"
                  >
                    <UserPlus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="p-4 bg-white border-b border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <div>
                    <Label htmlFor="academic-year" className="text-sm font-medium text-gray-600 mb-1.5 block">Academic Year</Label>
                    <Select value={academicYearFilter} onValueChange={setAcademicYearFilter}>
                      <SelectTrigger id="academic-year" className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                        <SelectValue placeholder="Select Academic Year" />
                      </SelectTrigger>
                      <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                        {academicYears.map((year) => (
                          <SelectItem key={year._id} value={year._id}>{year.startYear}-{year.endYear}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="subject-filter" className="text-sm font-medium text-gray-600 mb-1.5 block">Filter by Subject</Label>
                    <Select value={subjectFilter} onValueChange={setSubjectFilter}>
                      <SelectTrigger id="subject-filter" className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                        <SelectValue placeholder="Select Subject" />
                      </SelectTrigger>
                      <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                        <SelectItem value="all">All Subjects</SelectItem>
                        {subjectsList.map((subject) => (
                          <SelectItem key={subject._id} value={subject._id}>{subject.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="status-filter" className="text-sm font-medium text-gray-600 mb-1.5 block">Status</Label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger id="status-filter" className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="all">All</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="designation-filter" className="text-sm font-medium text-gray-600 mb-1.5 block">Designation</Label>
                    <Select value={designationFilter} onValueChange={setDesignationFilter}>
                      <SelectTrigger id="designation-filter" className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                        <SelectValue placeholder="Designation" />
                      </SelectTrigger>
                      <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                        <SelectItem value="all">All Designations</SelectItem>
                        {designations.map((d) => (
                          <SelectItem key={d._id} value={d._id}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="department-filter" className="text-sm font-medium text-gray-600 mb-1.5 block">Department</Label>
                    <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                      <SelectTrigger id="department-filter" className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                        <SelectValue placeholder="Department" />
                      </SelectTrigger>
                      <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                        <SelectItem value="all">All Departments</SelectItem>
                        {departments.map((d) => (
                          <SelectItem key={d._id} value={d._id}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="wing-filter" className="text-sm font-medium text-gray-600 mb-1.5 block">Wing</Label>
                    <Select value={wingFilter} onValueChange={setWingFilter}>
                      <SelectTrigger id="wing-filter" className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                        <SelectValue placeholder="Wing" />
                      </SelectTrigger>
                      <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                        <SelectItem value="all">All Wings</SelectItem>
                        {wings.map((w) => (
                          <SelectItem key={w._id} value={w._id}>{w.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {error && (
                <Alert variant="destructive" className="m-4 rounded-lg bg-red-50 text-red-800 border-red-200">
                  <AlertDescription className="font-medium">{error}</AlertDescription>
                </Alert>
              )}
              {successMessage && (
                <Alert className="m-4 rounded-lg bg-green-50 text-green-800 border-green-200">
                  <AlertDescription className="font-medium">{successMessage}</AlertDescription>
                </Alert>
              )}

              <ImportProgressDialog
                isOpen={showProgressDialog}
                progress={importProgress}
                totalRecords={importProgress.totalRecords}
                processedRecords={importProgress.processedRecords}
                successCount={importProgress.successCount}
                failureCount={importProgress.failureCount}
              />
              <ImportSuccessDialog
                isOpen={importSuccess}
                onClose={() => setImportSuccess(false)}
                imported={importProgress.successCount}
                failed={importProgress.failureCount}
                total={importProgress.totalRecords}
              />
              <TeacherImportManualDialog isOpen={isImportManualOpen} onClose={() => setIsImportManualOpen(false)} />

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-y border-gray-200">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort("name")}>
                        <div className="flex items-center gap-1">Teacher Name {getSortIcon("name")}</div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort("teacherCode")}>
                        <div className="flex items-center gap-1">Teacher Code {getSortIcon("teacherCode")}</div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Subjects</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {isLoading ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                          <div className="flex flex-col items-center justify-center gap-2">
                            <Loader2 className="h-5 w-5 animate-spin text-[#0263cc]" />
                            <span className="text-sm">Loading teachers...</span>
                          </div>
                        </td>
                      </tr>
                    ) : teachers.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-500">No teachers found</td>
                      </tr>
                    ) : (
                      teachers.map((teacher) => (
                        <tr key={teacher._id} className="hover:bg-gray-50 transition-colors" onClick={() => handleViewProfile(teacher)}>
                          <td className="px-4 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden">
                                {teacher.profileImage ? (
                                  <img src={teacher.profileImage || "/placeholder.svg"} alt={getFullName(teacher)} className="w-full h-full object-cover" />
                                ) : (
                                  <span className="text-blue-600 font-semibold">{getTeacherInitial(teacher)}</span>
                                )}
                              </div>
                              <span className="font-medium text-[#0263cc] hover:text-[#0263cc]/80">{getFullName(teacher)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">{teacher.employeeCode || teacher.teacherCode}</td>
                          <td className="px-4 py-4 text-sm text-gray-600">
                            {teacher.subjects?.length > 0 ? (
                              <>
                                {teacher.subjects.slice(0, 2).map((sub, index) => (
                                  <span key={index}>{sub.name}{index === 0 && teacher.subjects.length > 1 ? ", " : ""}</span>
                                ))}
                                {teacher.subjects.length > 2 && " ..."}
                              </>
                            ) : "Not assigned"}
                          </td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium capitalize ${
                              (teacher.status || "active") === "active" ? "bg-green-100 text-green-800"
                              : teacher.status === "inactive" ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-800"
                            }`}>
                              {teacher.status || "active"}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-gray-600 hover:text-[#0263cc]"
                              onClick={(e) => { e.stopPropagation(); handleViewProfile(teacher); }}
                            >
                              <ArrowRight className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={currentPage}
                totalItems={totalTeachers}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
                currentPageCount={teachers.length}
                itemLabel="teachers"
              />
            </CardContent>
          </Card>
        </>
      )}

      {/* Export Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>Export Preview</DialogTitle></DialogHeader>
          <div className="overflow-x-auto">
            {previewData && (
              <Table>
                <TableHeader>
                  <TableRow>
                    {Object.keys(exportColumns).filter((k) => exportColumns[k]).map((key) => (
                      <TableHead key={key}>{key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase())}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewData.map((row, index) => (
                    <TableRow key={index}>
                      {Object.keys(exportColumns).filter((k) => exportColumns[k]).map((key) => (
                        <TableCell key={key}>{row[key]}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <div className="flex justify-between mt-4">
            <p className="text-sm text-gray-500">Showing preview of first 5 records</p>
            <div className="flex space-x-2">
              <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>Close</Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button><Download className="w-4 h-4 mr-2" /> Export</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleExportCSV}><FileText className="w-4 h-4 mr-2" /> Export as CSV</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExportExcel("xlsx")}><FileSpreadsheet className="w-4 h-4 mr-2" /> Export as xlsx</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExportExcel("xls")}><FileSpreadsheet className="w-4 h-4 mr-2" /> Export as xls</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <EditProfileModal
        key={selectedTeacher?._id ?? "edit-modal"}
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setSelectedTeacher(null); }}
        teacher={selectedTeacher}
        onUpdate={handleUpdateTeacher}
        subjects={subjectsList}
        classes={classesData}
        sections={sectionData}
        academicYears={academicYears}
      />
      <DeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => {
          handleDeleteTeacher(selectedTeacherProfile._id);
          setSelectedTeacherProfile(null);
        }}
      />
      <CreateTeacherModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        currentAcademicYear={academicYears.find((year) => year.isCurrent) || null}
        subjects={subjectsList}
        fetchTeachers={fetchTeachers}
      />
      <Dialog open={isPermissionModalOpen} onOpenChange={setIsPermissionModalOpen}>
        <DialogContent className="max-w-lg">
          <TeacherPermissionModal
            teacherId={selectedTeacherProfile?._id}
            teacherName={selectedTeacherProfile ? getFullName(selectedTeacherProfile) : ""}
            isOpen={isPermissionModalOpen}
            onClose={() => setIsPermissionModalOpen(false)}
            onSuccess={() => {}}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TeachersContent;
